// src/components/AdminSpendView.tsx - Billing-ledger spend windows and top spenders.
import React, { memo, useState } from 'react';
import { DollarSign, Users, Contact, Receipt, ArrowUpRight } from 'lucide-react';
import { useAdminRead } from '../hooks/useAdminRead';
import AdminReadState from './AdminReadState';
import { formatLargeNumber } from '../utils';
import type { AdminSpendResponse, AdminSpendWindow } from '../types';
import './AdminDashboard.css';

// Rolling windows followed by the separate calendar month-to-date period.
const WINDOWS: { key: string; label: string; hint: string }[] = [
  { key: '1h', label: 'Last hour', hint: 'Rolling hour' },
  { key: '12h', label: 'Last 12 hours', hint: 'Rolling half day' },
  { key: '24h', label: 'Last 24 hours', hint: 'Rolling day' },
  { key: '48h', label: 'Last 48 hours', hint: 'Rolling two days' },
  { key: '7d', label: 'Last 7 days', hint: 'Rolling week' },
  { key: '30d', label: 'Last 30 days', hint: 'Rolling month' },
  { key: 'mtd', label: 'Month to date', hint: 'From the 1st' },
];

const usd = (value: number) => (value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`);
const contacts = (value: number) => formatLargeNumber(value);

const SpendStatCard = memo(({ label, value, sub, icon, isLoading }: {
  label: string; value: string; sub?: string; icon: React.ReactNode; isLoading: boolean;
}) => (
  <div className="admin-card admin-stat-card admin-card__body">
    <div className="admin-stat-card__header">
      <div className="admin-stat-card__icon">{icon}</div>
      <div className="admin-stat-card__label">{label}</div>
    </div>
    {isLoading ? <div className="skeleton-text" style={{ width: '90px', height: '36px', marginTop: '4px' }} /> : (
      <>
        <div className="admin-stat-card__value">{value}</div>
        {sub && <div className="admin-stat-card__label" style={{ marginTop: '2px' }}>{sub}</div>}
      </>
    )}
  </div>
));
SpendStatCard.displayName = 'SpendStatCard';

const WindowCard = memo(({ label, hint, window, isLoading, isReference }: {
  label: string; hint: string; window: AdminSpendWindow | undefined; isLoading: boolean; isReference: boolean;
}) => (
  <div className={`admin-card admin-stat-card admin-card__body${isReference ? ' admin-spend-card--reference' : ''}`}>
    <div className="admin-stat-card__header">
      <div className="admin-stat-card__label">{label}</div>
      {isReference && <span className="admin-spend-badge">Full period</span>}
    </div>
    {isLoading || !window ? (
      <div className="skeleton-text" style={{ width: '80px', height: '28px', marginTop: '6px' }} />
    ) : (
      <>
        <div className="admin-stat-card__value">{usd(window.spend)}</div>
        <dl className="admin-spend-meta">
          <div><dt>Contacts</dt><dd>{contacts(window.contacts_recorded)}</dd></div>
          <div><dt>Events</dt><dd>{formatLargeNumber(window.events)}</dd></div>
          <div><dt>Users</dt><dd>{formatLargeNumber(window.paying_users)}</dd></div>
        </dl>
        {!isReference && <p className="admin-spend-hint">{hint}</p>}
      </>
    )}
  </div>
));
WindowCard.displayName = 'WindowCard';

const AdminSpendView = memo(({ onUserClick }: { onUserClick: (userId: string) => void }) => {
  const [selected, setSelected] = useState('24h');
  const [topWindow, setTopWindow] = useState<'48h' | '30d'>('48h');
  const read = useAdminRead<AdminSpendResponse>(`/api/admin/spend?top_n=10&top_window=${topWindow}`);
  const data = read.stale ? null : read.data;
  const topLabel = topWindow === '48h' ? 'last 48 hours' : 'last 30 days';
  const loading = read.loading;

  const window = data?.windows?.[selected];
  const topUsers = data?.top_users ?? [];

  return (
    <div className="analytics-view">
      <AdminReadState {...read} hasData={!!data} />

      <div className="analytics-view__controls">
        <div className="segment-control">
          {WINDOWS.map(({ key, label }) => (
            <button key={key} className={`segment-button analytics-time-button ${selected === key ? 'active' : ''}`} onClick={() => setSelected(key)}>
              {label}
            </button>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" disabled={loading} onClick={read.refresh}>Refresh</button>
      </div>

      <p className="admin-footnote">
        Spend counts completed contact purchases at their recorded purchase cost, whether paid from balance or card.
        Top-ups, debt repayments, refunds and admin credits are excluded; older purchase events use their recorded amounts. Each window is aggregated separately, so rolling periods are comparable. Month to date starts on the 1st at 00:00 UTC. Contact counts reflect what is recorded on ledger events.
      </p>

      <div className="analytics-view__stat-grid">
        <SpendStatCard label={`Spend · ${WINDOWS.find(w => w.key === selected)?.label ?? selected}`} value={usd(window?.spend ?? 0)} sub={`${formatLargeNumber(window?.contacts_recorded ?? 0)} contacts unlocked`} icon={<DollarSign size={24} />} isLoading={loading || !data} />
        <SpendStatCard label="Contacts unlocked" value={contacts(window?.contacts_recorded ?? 0)} sub={`in ${WINDOWS.find(w => w.key === selected)?.label ?? selected}`} icon={<Contact size={24} />} isLoading={loading || !data} />
        <SpendStatCard label="Paying users" value={formatLargeNumber(window?.paying_users ?? 0)} sub={`${formatLargeNumber(window?.events ?? 0)} billed events`} icon={<Users size={24} />} isLoading={loading || !data} />
        <SpendStatCard label="Lifetime spend" value={usd(data?.lifetime_spend ?? 0)} sub={`over ${formatLargeNumber(data?.lifetime_events ?? 0)} events`} icon={<Receipt size={24} />} isLoading={loading || !data} />
      </div>

      <div className="admin-card">
        <div className="admin-card__header"><h3>Every window</h3></div>
        <div className="admin-card__body">
          <div className="admin-spend-grid">
            {WINDOWS.map(({ key, label, hint }) => (
              <WindowCard key={key} label={label} hint={hint} window={data?.windows?.[key]}
                isLoading={loading || !data} isReference={key === '30d'} />
            ))}
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__header"><h3>Top spenders · {topLabel}</h3>
          <select aria-label="Top spenders period" value={topWindow} onChange={e => setTopWindow(e.target.value as '48h' | '30d')}>
            <option value="48h">Last 48 hours</option><option value="30d">Last 30 days</option>
          </select>
        </div>
        <div className="admin-card__body">
          {loading && !data ? (
            <div className="skeleton-text" style={{ width: '100%', height: '240px', borderRadius: 'var(--radius)' }} />
          ) : !data ? (
            <p className="admin-notice">Spend data is unavailable. Retry to load the report.</p>
          ) : topUsers.length === 0 ? (
            <p className="admin-notice">No billed usage in the {topLabel}.</p>
          ) : (
            <div className="table-wrapper-condensed">
              <table className="table-premium">
                <thead>
                  <tr><th>Customer</th><th className="text-right">Spend ({topWindow})</th><th className="text-right">Billed events</th><th /></tr>
                </thead>
                <tbody>
                  {topUsers.map(user => (
                    <tr key={user.user_id}>
                      <td>{user.user_name || <code className="api-key-code">{user.user_id}</code>}</td>
                      <td className="text-right">{usd(user.spend)}</td>
                      <td className="text-right">{formatLargeNumber(user.events)}</td>
                      <td className="text-right">
                        <button className="btn btn-secondary btn-sm" onClick={() => onUserClick(user.user_id)}>
                          View <ArrowUpRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="admin-footnote" style={{ marginTop: 'var(--space-sm)' }}>
            Ranked by the largest amount spent first, across both balance and card payments. Only customers with billed
            spend appear; accounts with activity but no charges are not listed.
          </p>
        </div>
      </div>
    </div>
  );
});
AdminSpendView.displayName = 'AdminSpendView';

export default AdminSpendView;
